// Blocker Service — read/update the "what's getting in the way" annotations
// (demoted-roadblock model) recorded on a blockable hierarchy item.
// See newage-horton/docs/blocker-model.md.

export type BlockerStatus = 'active' | 'resolved' | 'dismissed'

export type BlockerItemType =
  | 'dream'
  | 'aspiration'
  | 'goal'
  | 'task'
  | 'skill'
  | 'habit'
  | 'action_item'

export type Blocker = {
  id: string
  reason: string
  type?: string
  severity?: 'low' | 'medium' | 'high' | string
  confidence?: number
  source?: string
  status?: BlockerStatus
  blocking_object_id?: string | null
  suggested_growth_item_ids?: string[]
  created_at?: string
  updated_at?: string
  // tolerate legacy/alternate shapes
  title?: string
}

export class BlockerService {
  private base(itemType: BlockerItemType, itemId: number | string) {
    return `/api/hierarchy/item/${itemType}/${itemId}/blockers`
  }

  async getBlockers(
    itemType: BlockerItemType,
    itemId: number | string,
  ): Promise<Blocker[]> {
    const response = await fetch(this.base(itemType, itemId), {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      // A missing item or unsupported type should surface as "no blockers"
      // rather than breaking the host page.
      return []
    }
    const result = await response.json().catch(() => ({}))
    const data = result?.data ?? result
    return Array.isArray(data) ? (data as Blocker[]) : []
  }

  async updateBlockerStatus(
    itemType: BlockerItemType,
    itemId: number | string,
    blockerId: string,
    status: BlockerStatus,
  ): Promise<Blocker[]> {
    const response = await fetch(
      `${this.base(itemType, itemId)}/${encodeURIComponent(blockerId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    )
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: 'Failed to update blocker' }))
      throw new Error(err.error || 'Failed to update blocker')
    }
    const result = await response.json().catch(() => ({}))
    const data = result?.data ?? result
    return Array.isArray(data) ? (data as Blocker[]) : []
  }

  /**
   * Start (or resume) a work-through session for a blocker and return the
   * backing roadblock id, which opens the staged workthrough/practice/
   * evidence engine for it.
   */
  async startWorkthrough(
    itemType: BlockerItemType,
    itemId: number | string,
    blockerId: string,
  ): Promise<{ roadblockId: number; created: boolean }> {
    const response = await fetch(
      `${this.base(itemType, itemId)}/${encodeURIComponent(blockerId)}/workthrough`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      },
    )
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ error: 'Failed to start work-through' }))
      throw new Error(err.error || 'Failed to start work-through')
    }
    const result = await response.json().catch(() => ({}))
    return {
      roadblockId: Number(result?.roadblock_id),
      created: Boolean(result?.created),
    }
  }
}

export const blockerService = new BlockerService()
